import { translateApiError, translateServerText } from './server-text';

/**
 * The API client.
 *
 * Three jobs, and nothing else:
 *   1. Attach the access token.
 *   2. Refresh it once, transparently, when the server says it expired — and
 *      queue concurrent callers behind that single refresh instead of firing one
 *      per in-flight request, which would burn the rotating refresh token and get
 *      the whole session revoked for reuse.
 *   3. Turn the API's error envelope into a typed Error the UI can branch on.
 */

const ACCESS_KEY = 'bedly.accessToken';
const REFRESH_KEY = 'bedly.refreshToken';

export type ApiErrorBody = {
  error: {
    code: string;
    message: string;
    details?: unknown;
    requestId: string;
  };
};

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly details?: unknown;
  readonly requestId?: string;

  constructor(status: number, body: ApiErrorBody | null, fallback: string) {
    super(translateApiError(body?.error?.message ?? fallback, body?.error?.code ?? 'UNKNOWN'));
    this.name = 'ApiError';
    this.status = status;
    this.code = body?.error?.code ?? 'UNKNOWN';
    this.details = body?.error?.details;
    this.requestId = body?.error?.requestId;
  }

  /** Field-level messages, when the server sent them. */
  fieldErrors(): Record<string, string> {
    const details = this.details;
    if (!Array.isArray(details)) return {};
    const result: Record<string, string> = {};
    for (const entry of details) {
      const item = entry as { field?: string; message?: string };
      if (item.field && item.message) result[item.field] = translateServerText(item.message);
    }
    return result;
  }
}

export const tokens = {
  access: (): string | null => localStorage.getItem(ACCESS_KEY),
  refresh: (): string | null => localStorage.getItem(REFRESH_KEY),
  set(access: string, refresh: string): void {
    localStorage.setItem(ACCESS_KEY, access);
    localStorage.setItem(REFRESH_KEY, refresh);
  },
  clear(): void {
    localStorage.removeItem(ACCESS_KEY);
    localStorage.removeItem(REFRESH_KEY);
  },
};

type Options = {
  method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';
  body?: unknown;
  /** Skip the Authorization header even when a token exists. */
  anonymous?: boolean;
  formData?: FormData;
  signal?: AbortSignal;
};

let refreshInFlight: Promise<boolean> | null = null;
const listeners = new Set<() => void>();

/** Notified when the session ends, so the app can drop the user to the login screen. */
export const onSessionLost = (listener: () => void): (() => void) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};

const loseSession = (): void => {
  tokens.clear();
  for (const listener of listeners) listener();
};

async function refreshTokens(): Promise<boolean> {
  // Collapse concurrent refreshes: the refresh token rotates, so two parallel
  // calls would make the second look like a replay and revoke the family.
  refreshInFlight ??= (async () => {
    const refreshToken = tokens.refresh();
    if (!refreshToken) return false;
    try {
      const response = await fetch('/v1/auth/refresh', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ refreshToken }),
      });
      if (!response.ok) return false;
      const data = (await response.json()) as { accessToken: string; refreshToken: string };
      tokens.set(data.accessToken, data.refreshToken);
      return true;
    } catch {
      return false;
    } finally {
      // Cleared on the next tick so callers awaiting this promise all see it.
      setTimeout(() => {
        refreshInFlight = null;
      }, 0);
    }
  })();
  return refreshInFlight;
}

async function send<T>(path: string, options: Options, isRetry = false): Promise<T> {
  const access = tokens.access();
  const headers: Record<string, string> = {};
  if (options.body !== undefined) headers['content-type'] = 'application/json';
  if (access && !options.anonymous) headers.authorization = `Bearer ${access}`;

  const response = await fetch(`/v1${path}`, {
    method: options.method ?? 'GET',
    headers,
    ...(options.formData
      ? { body: options.formData }
      : options.body !== undefined
        ? { body: JSON.stringify(options.body) }
        : {}),
    ...(options.signal ? { signal: options.signal } : {}),
  });

  if (response.status === 204) return undefined as T;

  const text = await response.text();
  let payload: unknown = null;
  try {
    payload = text ? JSON.parse(text) : null;
  } catch {
    payload = null;
  }

  if (response.ok) return payload as T;

  const body = payload as ApiErrorBody | null;
  const code = body?.error?.code;

  // One transparent retry for an expired access token.
  const expired = response.status === 401 && (code === 'UNAUTHENTICATED' || code === 'TOKEN_EXPIRED');
  if (expired && !isRetry && !options.anonymous && tokens.refresh()) {
    const refreshed = await refreshTokens();
    if (refreshed) return send<T>(path, options, true);
    loseSession();
  }

  // A revoked family means the session is gone for good: no retry, straight out.
  if (code === 'TOKEN_REUSED') loseSession();

  throw new ApiError(response.status, body, `Request failed with ${response.status}`);
}

export const api = {
  get: <T>(path: string, options: Omit<Options, 'method' | 'body'> = {}) =>
    send<T>(path, { ...options, method: 'GET' }),
  post: <T>(path: string, body?: unknown, options: Omit<Options, 'method' | 'body'> = {}) =>
    send<T>(path, { ...options, method: 'POST', body: body ?? {} }),
  patch: <T>(path: string, body?: unknown, options: Omit<Options, 'method' | 'body'> = {}) =>
    send<T>(path, { ...options, method: 'PATCH', body: body ?? {} }),
  put: <T>(path: string, body?: unknown, options: Omit<Options, 'method' | 'body'> = {}) =>
    send<T>(path, { ...options, method: 'PUT', body: body ?? {} }),
  delete: <T>(path: string, body?: unknown, options: Omit<Options, 'method' | 'body'> = {}) =>
    send<T>(path, { ...options, method: 'DELETE', body: body ?? {} }),
  upload: <T>(path: string, formData: FormData) =>
    send<T>(path, { method: 'POST', formData }),
};

/** Query-string builder that drops empty values instead of sending `?city=`. */
export const qs = (params: Record<string, unknown>): string => {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null || value === '' || value === false) continue;
    search.set(key, Array.isArray(value) ? value.join(',') : String(value));
  }
  const rendered = search.toString();
  return rendered ? `?${rendered}` : '';
};
